import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Close as RejectIcon
} from '@mui/icons-material';
import { useSuggestions } from '../hooks';
import { useApproval } from '../hooks';
import { useNotification } from '../../shared/hooks';
import { StatusFilter } from '../../shared/components';
import { RejectionDialog } from '../components';
import PaginationControls from '../../components/PaginationControls';

/**
 * SuggestionListTable page component
 * Displays suggestions in a table format matching FoodManager layout
 * Shows all nutritional details and metadata
 */
const SuggestionListTable = ({
  type = 'food', // 'food', 'foodLabel', 'recipe'
  title = 'Suggestions',
  description = 'Review and manage user-submitted suggestions',
  basePath = '/suggestions'
}) => {
  const navigate = useNavigate();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  // Hooks
  const {
    suggestions,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    refetch
  } = useSuggestions(type);

  const { approve, reject, loading: actionLoading } = useApproval(type);
  const { snackbar, showNotification, hideNotification } = useNotification();

  // Handle view details - navigate to detail page
  const handleViewDetails = (suggestion) => {
    navigate(`${basePath}/${suggestion.id}`);
  };

  // Handle quick approve
  const handleQuickApprove = async (suggestion) => {
    const { food, recipe } = suggestion;
    const item = food || recipe;

    // Prepare minimal data for quick approval
    const approvalData = {
      name: item.name,
      categoryId: item.categoryId,
      category: item.category,
      description: item.description,
      calories: item.calories,
      protein: item.protein,
      fat: item.fat,
      carbs: item.carbs,
      glycemicIndex: item.glycemicIndex,
      unitType: item.unitType || 'g',
      unitValue: item.unitValue || 100,
      portionSize: item.portionSize,
      image: item.image,
      languageSpec: item.languageSpec,
      tagIds: item.tags?.map(t => t.id) || [],
      isCustom: item.isCustom,
      showOriginalAmount: item.showOriginalAmount
    };

    const result = await approve(suggestion.id, approvalData);

    if (result.success) {
      showNotification(`${type === 'recipe' ? 'Recipe' : 'Food'} approved successfully!`, 'success');
      refetch();
    } else {
      showNotification(result.error || 'Failed to approve suggestion', 'error');
    }
  };

  // Handle quick reject - open dialog
  const handleQuickReject = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setRejectDialogOpen(true);
  };

  // Confirm rejection with reason
  const handleConfirmReject = async (reason) => {
    if (!selectedSuggestion) return;

    const result = await reject(selectedSuggestion.id, reason);

    if (result.success) {
      showNotification(`${type === 'recipe' ? 'Recipe' : 'Food'} rejected`, 'success');
      setRejectDialogOpen(false);
      setSelectedSuggestion(null);
      refetch();
    } else {
      showNotification(result.error || 'Failed to reject suggestion', 'error');
    }
  };

  // Handle status filter change
  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <StatusFilter
            value={statusFilter}
            onChange={handleStatusChange}
            label="Filter by Status"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {totalCount} {totalCount === 1 ? 'result' : 'results'}
          </Typography>
        </Stack>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && suggestions.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No suggestions found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try changing the status filter or check back later.
          </Typography>
        </Paper>
      )}

      {/* Table */}
      {!loading && !error && suggestions.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 'calc(100vh - 300px)',
              '& .MuiTableHead-root .MuiTableCell-root': {
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'primary.light'
              }
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Actions
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Image
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Description
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Calories
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Protein (g)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Fat (g)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Carbs (g)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    GI
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Unit
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Submitted By
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                    Submitted At
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suggestions.map((suggestion) => {
                  const item = suggestion.food || suggestion.recipe;
                  if (!item) return null;

                  const imageUrl = item.image || 'https://via.placeholder.com/50x50/e0e0e0/757575?text=No+Image';

                  return (
                    <TableRow key={suggestion.id} hover>
                      {/* Actions */}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(suggestion)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {suggestion.status === 'pending' && (
                            <>
                              <Tooltip title="Quick Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleQuickApprove(suggestion)}
                                  disabled={actionLoading}
                                >
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Quick Reject">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleQuickReject(suggestion)}
                                  disabled={actionLoading}
                                >
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={suggestion.status || 'pending'}
                          color={getStatusColor(suggestion.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                        />
                      </TableCell>

                      {/* Image */}
                      <TableCell>
                        <Avatar
                          src={imageUrl}
                          alt={item.name}
                          sx={{
                            width: 50,
                            height: 50,
                            border: item.image ? '2px solid #e0e0e0' : '2px dashed #bdbdbd'
                          }}
                          variant="rounded"
                        />
                      </TableCell>

                      {/* Name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.name || '—'}
                        </Typography>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Chip
                          label={item.category || 'Uncategorized'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: 200
                          }}
                        >
                          {item.description || '—'}
                        </Typography>
                      </TableCell>

                      {/* Calories */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.calories != null ? parseFloat(item.calories).toFixed(1) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Protein */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.protein != null ? parseFloat(item.protein).toFixed(1) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Fat */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.fat != null ? parseFloat(item.fat).toFixed(1) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Carbs */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.carbs != null ? parseFloat(item.carbs).toFixed(1) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Glycemic Index */}
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.glycemicIndex != null ? parseFloat(item.glycemicIndex).toFixed(0) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Unit */}
                      <TableCell align="center">
                        <Typography variant="caption">
                          {item.unitValue || 100} {item.unitType || 'g'}
                        </Typography>
                      </TableCell>

                      {/* Submitted By */}
                      <TableCell>
                        <Typography variant="caption">
                          {suggestion.user?.username || suggestion.user?.email || 'Unknown'}
                        </Typography>
                      </TableCell>

                      {/* Submitted At */}
                      <TableCell align="center">
                        <Typography variant="caption">
                          {formatDate(suggestion.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <PaginationControls
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[50, 100, 200, 500]}
            />
          </Box>
        </>
      )}

      {/* Rejection Dialog */}
      <RejectionDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setSelectedSuggestion(null);
        }}
        onConfirm={handleConfirmReject}
        loading={actionLoading}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={hideNotification}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuggestionListTable;
